import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Phone, PhoneMissed, PhoneIncoming } from "lucide-react";
import { DEMO, IVR_SCRIPT, SMS } from "@/components/walkthrough/data";

export function PhoneFrame({ children, label }) {
  return (
    <div className="w-[290px] h-[500px] rounded-[2.5rem] border-[10px] border-slate-800 bg-slate-950 shadow-2xl overflow-hidden flex flex-col mx-auto">
      <div className="h-7 flex items-center justify-center text-[10px] text-slate-500 font-mono">{label}</div>
      <div className="flex-1 overflow-hidden">{children}</div>
    </div>
  );
}

export function CallStep() {
  return (
    <PhoneFrame label="Homeowner's phone">
      <div className="h-full flex flex-col items-center justify-center gap-4 bg-gradient-to-b from-slate-900 to-slate-950" data-testid="wt-call">
        <p className="text-slate-400 text-sm">Calling…</p>
        <p className="text-white text-2xl font-display font-bold">{DEMO.company}</p>
        <motion.div animate={{ scale: [1, 1.15, 1] }} transition={{ repeat: Infinity, duration: 1.2 }}
          className="w-20 h-20 rounded-full bg-emerald-500/20 flex items-center justify-center mt-6">
          <PhoneIncoming className="w-9 h-9 text-emerald-400" />
        </motion.div>
        <p className="text-xs text-slate-500 mt-6">{DEMO.homeowner} · {DEMO.phone}</p>
      </div>
    </PhoneFrame>
  );
}

export function MissedStep() {
  return (
    <div className="grid sm:grid-cols-2 gap-6 items-center max-w-3xl mx-auto" data-testid="wt-missed">
      <div className="rounded-2xl border border-slate-800 bg-slate-900 p-6">
        <PhoneMissed className="w-10 h-10 text-rose-400 mb-3" />
        <p className="text-white font-semibold text-lg">Missed call from {DEMO.phone}</p>
        <p className="text-slate-400 text-sm mt-1">The crew is up on a roof and can't answer.</p>
      </div>
      <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.6 }}
        className="rounded-2xl border border-orange-500/40 bg-orange-500/10 p-6">
        <Phone className="w-10 h-10 text-orange-400 mb-3" />
        <p className="text-white font-semibold text-lg">RoofLead takes over</p>
        <p className="text-slate-300 text-sm mt-1">Within seconds, the caller hears {DEMO.company}'s automated message.</p>
      </motion.div>
    </div>
  );
}

export function ConsentStep() {
  const [pressed, setPressed] = useState(false);
  useEffect(() => { const t = setTimeout(() => setPressed(true), 1800); return () => clearTimeout(t); }, []);
  return (
    <div className="grid md:grid-cols-[1fr_300px] gap-8 items-center max-w-4xl mx-auto" data-testid="wt-consent">
      <div className="rounded-2xl border border-slate-800 bg-slate-900 p-6">
        <p className="font-mono text-xs uppercase tracking-widest text-orange-400 mb-3">Automated phone message</p>
        <p className="text-slate-200 leading-relaxed">"{IVR_SCRIPT}"</p>
        <p className="text-xs text-slate-500 mt-4">Consent is recorded with the time, number and exact wording before any text is sent.</p>
      </div>
      <PhoneFrame label="Keypad">
        <div className="h-full grid grid-cols-3 gap-3 p-6 content-center">
          {["1", "2", "3", "4", "5", "6", "7", "8", "9", "*", "0", "#"].map((k) => (
            <div key={k} data-testid={k === "1" ? "wt-key-1" : undefined}
              className={`aspect-square rounded-full flex items-center justify-center text-xl font-semibold transition-colors duration-300 ${k === "1" && pressed ? "bg-emerald-500 text-slate-900 ring-4 ring-emerald-400/40" : "bg-slate-800 text-slate-200"}`}>{k}</div>
          ))}
          <p className="col-span-3 text-center text-xs mt-2 text-emerald-300 h-4">{pressed ? "Pressed 1: consent recorded" : ""}</p>
        </div>
      </PhoneFrame>
    </div>
  );
}

export function SmsStep() {
  const [shown, setShown] = useState(1);
  useEffect(() => {
    if (shown >= SMS.length) return;
    const t = setTimeout(() => setShown((s) => s + 1), 900);
    return () => clearTimeout(t);
  }, [shown]);
  return (
    <PhoneFrame label={`Text messages · ${DEMO.company}`}>
      <div className="h-full overflow-y-auto p-3 space-y-2 flex flex-col justify-end" data-testid="wt-sms">
        {SMS.slice(0, shown).map(([who, text], i) => (who === "sim" ? (
          <motion.div key={i} initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="wt-sms-simulated"
            className="self-center my-1 px-3 py-1 rounded-full border border-amber-400/40 bg-amber-400/10 text-amber-200 text-[10px] font-mono uppercase tracking-wider text-center">
            {text}
          </motion.div>
        ) : (
          <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
            className={`max-w-[85%] px-3 py-2 rounded-2xl text-[12.5px] leading-snug whitespace-pre-line ${who === "ai" ? "bg-slate-800 text-slate-100 self-start rounded-bl-sm" : "bg-orange-500 text-slate-900 self-end rounded-br-sm"}`}>
            {text}
          </motion.div>
        )))}
      </div>
    </PhoneFrame>
  );
}
